// Demo bill inbox. Follows ledger.approve_inbox_bill: no line may stay on the
// suspense account, and a bill whose lines don't add up to what the invoice
// says needs an explicit "post anyway".
import { formatMoney, toUnits } from '@elysian/ui';
import { demoStore } from './mockDocuments';
import type { DocumentInput, InboxItem, InboxStatus, LedgerDocument, LedgerSource } from './source';

type InboxSource = Pick<LedgerSource, 'inbox' | 'inboxAddress' | 'newInboxAddress' | 'inboxItem' | 'setInboxSupplier' | 'approveInbox' | 'rejectInbox' | 'attachmentFile' | 'retryInbox'>;

const delay = <T,>(v: T, ms = 80): Promise<T> => new Promise((r) => setTimeout(() => r(structuredClone(v)), ms));
const fail = (detail: string) => Promise.reject(new Error(detail));

interface ReadLine { description: string; quantity?: string; unit_price: string; tax_code: string; tax_amount?: string; account_code?: string }
interface Read {
  supplier: { name: string; vat_number?: string; email?: string };
  number?: string; issue?: string; due?: string; net?: string; vat?: string; gross?: string; currency?: string;
  conf: Record<string, number>;
  lines: ReadLine[];
}
interface Entry { item: InboxItem; read: Read | null; queuedAt?: number }

/**
 * The demo has no virus scanner or DocuLens, so uploaded bills walk through
 * the first steps by the clock: checked for viruses for two seconds, then
 * "being read", where they stay. Tests can move the clock on.
 */
export const pipelineClock = { now: () => Date.now(), scanMs: 2000 };

let entries: Entry[] = [];

function fieldsOf(r: Read): InboxItem['fields'] {
  const c = (k: string) => r.conf[k] ?? r.conf.overall ?? 0.5;
  const f: NonNullable<InboxItem['fields']> = {};
  const put = (k: string, v: string | undefined, ck: string) => { if (v) f[k] = { value: v, confidence: c(ck) }; };
  put('supplier_name', r.supplier.name, 'supplier');
  put('supplier_vat_number', r.supplier.vat_number, 'supplier');
  put('invoice_number', r.number, 'invoice_number');
  put('issue_date', r.issue, 'issue_date');
  put('due_date', r.due, 'due_date');
  put('net', r.net, 'totals');
  put('vat', r.vat, 'totals');
  put('gross', r.gross, 'totals');
  put('currency', r.currency, 'totals');
  return f;
}

function draftFor(r: Read, contactId: string): string {
  const input: DocumentInput = {
    type: 'purchase_bill', contact_id: contactId, issue_date: r.issue ?? '2026-10-11', due_date: r.due, supplier_ref: r.number,
    // As ledger.draft_from_extraction: the currency read off the bill, else the supplier's.
    currency: r.currency ?? demoStore.contacts.find((c) => c.id === contactId)?.currency ?? 'EUR',
    lines: r.lines.map((l) => ({ description: l.description, quantity: l.quantity ?? '1', unit_price: l.unit_price, account_code: l.account_code ?? '9999', tax_code: l.tax_code, tax_amount: l.tax_amount })),
  };
  return demoStore.createDraft(input);
}

/** What the database's checks would say about a draft bill. */
function warningsFor(e: Entry): string[] {
  const w: string[] = [];
  const id = e.item.draft_document_id;
  if (!id || !e.read) return w;
  const d = demoStore.get(id)!;
  const onSuspense = d.lines.filter((l) => l.account_code === '9999').length;
  if (onSuspense) w.push(`${onSuspense} ${onSuspense === 1 ? 'line needs' : 'lines need'} an account`);
  if (e.read.gross && Math.abs(Number(toUnits(d.gross_total) - toUnits(e.read.gross))) > 200) {
    w.push(`The lines come to ${formatMoney(d.gross_total, d.currency)} but the invoice says ${formatMoney(e.read.gross, d.currency)}`);
  }
  return w;
}

function view(e: Entry): InboxItem {
  if (e.queuedAt !== undefined && e.item.status === 'received' && pipelineClock.now() - e.queuedAt >= pipelineClock.scanMs) {
    e.item = { ...e.item, status: 'processing', scan_status: 'clean' };
  }
  const item = { ...e.item };
  item.warnings = warningsFor(e);
  if (item.draft_document_id && (item.status === 'needs_review' || item.status === 'ready')) {
    item.status = item.warnings.length ? 'needs_review' : 'ready';
  }
  return item;
}

function seed() {
  entries = [];
  const add = (item: Omit<InboxItem, 'fields' | 'warnings'>, read: Read | null, contactId?: string) => {
    const e: Entry = { item: { ...item, fields: read ? fieldsOf(read) : {}, warnings: [] }, read };
    if (read && contactId) e.item.draft_document_id = draftFor(read, contactId);
    entries.push(e);
  };
  add({ id: 'x1', status: 'ready', attachment_id: 'f1', file_name: 'ElectricIreland_Oct26.pdf', source: 'email', email_from: 'business@electricireland.ie',
    received_at: '2026-10-09T07:12:00Z', document_kind: 'invoice', confidence: 0.94, matched_contact_id: 's3', match_method: 'vat_number' },
  { supplier: { name: 'ELECTRIC IRELAND', vat_number: 'IE 8F52100V' }, number: 'EI-55102934', issue: '2026-10-07', due: '2026-10-21', net: '212.40', vat: '28.67', gross: '241.07',
    conf: { overall: 0.94, invoice_number: 0.98, totals: 0.97, supplier: 0.99 },
    lines: [{ description: 'Electricity 1 Aug – 30 Sep, Unit 4', unit_price: '212.40', tax_code: 'R135', account_code: '7100' }] }, 's3');
  add({ id: 'x2', status: 'needs_review', attachment_id: 'f2', file_name: 'DW-90412.pdf', source: 'email', email_from: 'accounts@dw.ie',
    received_at: '2026-10-10T16:40:00Z', document_kind: 'invoice', confidence: 0.91, matched_contact_id: 's1', match_method: 'vat_number' },
  { supplier: { name: 'DETECTOR WHOLESALE LIMITED', vat_number: 'IE 9876543 W' }, number: 'DW-90412', issue: '2026-10-08', due: '2026-11-07', net: '375.00', vat: '86.25', gross: '461.25',
    conf: { overall: 0.91, invoice_number: 0.99, totals: 0.95, supplier: 0.98, due_date: 0.66 },
    lines: [{ description: 'Optical smoke detector EV-PS', quantity: '20', unit_price: '18.00', tax_code: 'S23' }, { description: 'Delivery', unit_price: '15.00', tax_code: 'S23' }] }, 's1');
  add({ id: 'x3', status: 'needs_review', attachment_id: 'f3', file_name: 'CircleK-statement-0930.pdf', source: 'email', email_from: 'fuelcards@circlek.ie',
    received_at: '2026-10-08T18:40:00Z', document_kind: 'invoice', confidence: 0.78, matched_contact_id: 's4', match_method: 'alias' },
  { supplier: { name: 'Circle K Ireland Energy' }, number: 'CK-0930-2231', issue: '2026-09-30', due: '2026-10-30', net: '318.70', vat: '73.30', gross: '392.00',
    conf: { overall: 0.78, invoice_number: 0.88, totals: 0.64, supplier: 0.9 },
    lines: [{ description: 'Diesel, September (fuel card 4471)', unit_price: '317.70', tax_code: 'S23', tax_amount: '73.30', account_code: '7200' }] }, 's4');
  add({ id: 'x4', status: 'needs_supplier', attachment_id: 'f4', file_name: 'IMG_4471.jpg', source: 'mobile', email_from: null,
    received_at: '2026-10-11T09:03:00Z', document_kind: 'receipt', confidence: 0.69, matched_contact_id: null, match_method: null },
  { supplier: { name: 'PLUMB CENTER NAAS', vat_number: 'IE 6388047V' }, number: '0045-118822', issue: '2026-10-10', net: '54.47', vat: '12.53', gross: '67.00',
    conf: { overall: 0.69, supplier: 0.55, invoice_number: 0.42, totals: 0.81 },
    lines: [{ description: 'Pipe fittings and cable clips', unit_price: '54.47', tax_code: 'S23' }] });
  add({ id: 'x7', status: 'ready', attachment_id: 'f7', file_name: 'Anxin_PI-2026-0931.pdf', source: 'email', email_from: 'sales@anxin-sensor.cn',
    received_at: '2026-10-10T02:15:00Z', document_kind: 'invoice', confidence: 0.92, matched_contact_id: 's7', match_method: 'name' },
  { supplier: { name: 'SHENZHEN ANXIN SENSOR CO., LTD' }, number: 'PI-2026-0931', issue: '2026-10-09', due: '2026-11-08',
    net: '7400.00', vat: '0.00', gross: '7400.00', currency: 'CNY',
    conf: { overall: 0.92, invoice_number: 0.97, totals: 0.95, supplier: 0.93 },
    lines: [{ description: 'Photoelectric smoke sensor heads AX-210', quantity: '400', unit_price: '18.50', tax_code: 'Z', account_code: '5000' }] }, 's7');
  add({ id: 'x6', status: 'failed', attachment_id: 'f6', file_name: 'Screwfix_INV-4471.pdf', source: 'email', email_from: 'invoices@screwfix.ie',
    received_at: '2026-10-07T11:02:00Z', document_kind: null, confidence: null, matched_contact_id: null, match_method: null, scan_status: 'clean',
    error: 'DocuLens couldn’t be reached, so this bill hasn’t been read. Try again later, or enter it by hand.', can_retry: true }, null);
  add({ id: 'x5', status: 'processing', attachment_id: 'f5', file_name: 'Vodafone_Oct.pdf', source: 'email', email_from: 'ebill@vodafone.ie',
    received_at: '2026-10-11T13:20:00Z', document_kind: null, confidence: null, matched_contact_id: null, match_method: null }, null);
}
seed();

/** Bills not yet dealt with (received up to a day, if given), for the year- and month-end checklists. */
export function openInboxCount(upTo?: string) {
  return entries.filter((e) => !['approved', 'rejected'].includes(e.item.status)
    && (!upTo || e.item.received_at.slice(0, 10) <= upTo)).length;
}

/** Bills waiting for a person (as ledger.shell_status): a supplier to choose, a check, or ready to approve. */
export function inboxToCheck() {
  return entries.map(view).filter((i) => ['needs_supplier', 'needs_review', 'ready'].includes(i.status)).length;
}

/** A bill dropped into the inbox in the demo. There is no reader here, so it waits as "Being read". */
export function addUploadedBill(attachmentId: string, fileName: string, receivedAt: string): string {
  const id = `x${entries.length + 1}u${Date.now() % 100000}`;
  entries.push({ item: { id, status: 'received', scan_status: 'pending', attachment_id: attachmentId, file_name: fileName, source: 'upload', email_from: null,
    received_at: receivedAt, document_kind: null, confidence: null, matched_contact_id: null, match_method: null, fields: {}, warnings: [] },
  read: null, queuedAt: pipelineClock.now() });
  return id;
}

/** Whether a file is a bill still waiting in the inbox (it can't be attached elsewhere until dealt with). */
export function inboxHolds(attachmentId: string): string | null {
  return entries.find((e) => e.item.attachment_id === attachmentId && !['approved', 'rejected'].includes(e.item.status))?.item.id ?? null;
}

// The demo books' address for bills (#22): a 10-character code, the product, the platform's domain.
const demoCode = () => Array.from({ length: 10 }, () => 'abcdefghijklmnopqrstuvwxyz0123456789'[Math.floor(Math.random() * 36)]).join('');
let address = { address: 'k7m2qa9xwe.ledger@sainttronics.com', allowed_senders: [] as string[], created_at: '2026-10-17T18:00:00Z' };

export function resetDemoInbox() {
  seed();
  address = { address: 'k7m2qa9xwe.ledger@sainttronics.com', allowed_senders: [], created_at: '2026-10-17T18:00:00Z' };
}

export const demoInbox: InboxSource = {
  inboxAddress: () => delay(address),
  newInboxAddress: (allowedSenders = []) => {
    address = { address: `${demoCode()}.ledger@sainttronics.com`, allowed_senders: allowedSenders, created_at: new Date().toISOString() };
    return delay(address);
  },
  inbox: (status?: InboxStatus) => delay({
    items: entries
      .filter((e) => (status ? e.item.status === status : e.item.status !== 'approved' && e.item.status !== 'rejected'))
      .sort((a, b) => b.item.received_at.localeCompare(a.item.received_at))
      .map(view),
    usage: { month: '2026-10', used: 37, allowance: 200 },
  }),
  inboxItem: (id) => {
    const e = entries.find((x) => x.item.id === id);
    return e ? delay(view(e)) : fail('That bill is no longer in the inbox.');
  },
  setInboxSupplier: (id, choice) => {
    const e = entries.find((x) => x.item.id === id);
    if (!e || e.item.status !== 'needs_supplier' || !e.read) return fail('This bill isn’t waiting for a supplier.');
    let contactId: string;
    if ('contact_id' in choice) {
      if (!demoStore.contacts.some((c) => c.id === choice.contact_id && c.is_supplier)) return fail('Choose a supplier.');
      contactId = choice.contact_id;
    } else {
      contactId = `s${Date.now()}`;
      demoStore.addContact({ id: contactId, name: titleCase(e.read.supplier.name), vat_number: e.read.supplier.vat_number?.replace(/\s/g, ''),
        is_customer: false, is_supplier: true, is_active: true, balance: '0', country: 'IE', payment_terms_days: 30 });
    }
    e.item = { ...e.item, status: 'needs_review', matched_contact_id: contactId, match_method: 'person', draft_document_id: draftFor(e.read, contactId) };
    return delay(view(e));
  },
  approveInbox: (id, ack) => {
    const e = entries.find((x) => x.item.id === id);
    const docId = e?.item.draft_document_id;
    if (!e || !docId || !['ready', 'needs_review'].includes(e.item.status)) return fail('This bill has no draft to approve.');
    const d = demoStore.get(docId)!;
    const onSuspense = d.lines.filter((l) => l.account_code === '9999').length;
    if (onSuspense) return fail(`${onSuspense} line(s) still need an account`);
    if (!ack && e.read?.gross && Math.abs(Number(toUnits(d.gross_total) - toUnits(e.read.gross))) > 200) {
      return fail(`The bill comes to ${formatMoney(d.gross_total)} but the invoice says ${formatMoney(e.read.gross)}. Correct it, or confirm to post anyway.`);
    }
    const posted = demoStore.post(docId);
    e.item = { ...e.item, status: 'approved' };
    const { payments: _p, ...doc } = posted;
    return delay(doc as LedgerDocument);
  },
  rejectInbox: async (id, reason) => {
    const e = entries.find((x) => x.item.id === id);
    if (!e || e.item.status === 'approved' || e.item.status === 'rejected') return fail('This bill can’t be rejected.');
    if (reason.trim().length < 3) return fail('Give a reason (at least three characters).');
    if (e.item.draft_document_id) demoStore.remove(e.item.draft_document_id);
    e.item = { ...e.item, status: 'rejected', draft_document_id: null };
    await delay(null);
  },
  attachmentFile: () => Promise.resolve(null),
  retryInbox: (id) => {
    const e = entries.find((x) => x.item.id === id);
    if (!e) return fail('That bill is no longer in the inbox.');
    if (e.item.status !== 'failed') return fail('Only bills that couldn’t be read can be tried again');
    if (e.item.scan_status === 'infected') return fail('This file failed the virus scan and can’t be read. Ask the supplier for a clean copy.');
    e.item = { ...e.item, status: 'received', error: null, can_retry: false, scan_status: 'clean' };
    e.queuedAt = pipelineClock.now() - pipelineClock.scanMs / 2;
    return delay(view(e));
  },
};

function titleCase(s: string) {
  return s.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

